import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Search, X, Truck, IndianRupee, CalendarDays, FileText, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useSessionState } from '../hooks/useSessionState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 25;
const ALLOWED_ROLES = ['Super Admin', 'Admin Head', 'Admin Team'];

const STATUS_VARIANT = {
  Dispatched: 'outline',
  Delivered: 'default',
  Returned: 'destructive',
};

const emptyForm = {
  dispatch_date: new Date().toISOString().slice(0, 10),
  request_raised_by: '',
  received_by: '',
  drop_delivery_location: '',
  courier_type: '',
  request_type: 'Outgoing',
  awb_tracking_number: '',
  courier_partner: '',
  staff_member_name: '',
  actual_delivery_date: '',
  status: 'Dispatched',
  document_type: '',
  cost: '',
};

export default function CourierDetailsPage() {
  const { getAuthHeader, employee } = useAuth();
  const role = employee?.role || '';
  const hasAccess = ALLOWED_ROLES.includes(role);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useSessionState('couriers:page', 1);
  const [loading, setLoading] = useState(true);
  const [opts, setOpts] = useState({ courier_partners: [], statuses: [], document_types: [], request_types: [], default_request_raised_by: '' });

  // Filters (persisted)
  const [searchQuery, setSearchQuery] = useSessionState('couriers:search', '');
  const [dateFrom, setDateFrom] = useSessionState('couriers:dateFrom', '');
  const [dateTo, setDateTo] = useSessionState('couriers:dateTo', '');
  const [statusFilter, setStatusFilter] = useSessionState('couriers:status', '');
  const [partnerFilter, setPartnerFilter] = useSessionState('couriers:partner', '');
  const [docTypeFilter, setDocTypeFilter] = useSessionState('couriers:docType', '');

  // Form / dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  const fetchOptions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/couriers/options`, { headers: getAuthHeader() });
      if (res.ok) setOpts(await res.json());
    } catch { /* ignore */ }
  }, [getAuthHeader]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('page_size', PAGE_SIZE);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (statusFilter) params.set('status', statusFilter);
      if (partnerFilter) params.set('courier_partner', partnerFilter);
      if (docTypeFilter) params.set('document_type', docTypeFilter);
      if (searchQuery) params.set('search', searchQuery);
      const res = await fetch(`${API}/couriers?${params.toString()}`, { headers: getAuthHeader() });
      if (res.ok) {
        const d = await res.json();
        setItems(d.items || []);
        setTotal(d.total || 0);
      } else if (res.status === 403) {
        // no-op (UI already handles)
      }
    } catch (e) {
      toast.error('Failed to load courier records');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, page, dateFrom, dateTo, statusFilter, partnerFilter, docTypeFilter, searchQuery]);

  useEffect(() => {
    if (!hasAccess) return;
    fetchOptions();
  }, [fetchOptions, hasAccess]);

  useEffect(() => {
    if (!hasAccess) return;
    fetchItems();
  }, [fetchItems, hasAccess]);

  const openCreateDialog = () => {
    setEditingId(null);
    setForm({ ...emptyForm, request_raised_by: opts.default_request_raised_by || '' });
    setDialogOpen(true);
  };

  const openEditDialog = (c) => {
    setEditingId(c.courier_id);
    setForm({
      dispatch_date: c.dispatch_date || '',
      request_raised_by: c.request_raised_by || '',
      received_by: c.received_by || '',
      drop_delivery_location: c.drop_delivery_location || '',
      courier_type: c.courier_type || '',
      request_type: c.request_type || 'Outgoing',
      awb_tracking_number: c.awb_tracking_number || '',
      courier_partner: c.courier_partner || '',
      staff_member_name: c.staff_member_name || '',
      actual_delivery_date: c.actual_delivery_date || '',
      status: c.status || 'Dispatched',
      document_type: c.document_type || '',
      cost: c.cost != null ? String(c.cost) : '',
    });
    setViewItem(null);
    setDialogOpen(true);
  };

  const submitForm = async () => {
    // Basic client validation for required fields
    const required = ['dispatch_date', 'request_raised_by', 'received_by', 'drop_delivery_location', 'courier_type', 'courier_partner'];
    for (const k of required) {
      if (!String(form[k] || '').trim()) {
        toast.error(`Please fill: ${k.replace(/_/g, ' ')}`);
        return;
      }
    }
    if (form.courier_partner === 'Staff' && !String(form.staff_member_name || '').trim()) {
      toast.error('Please fill: Name of Staff Member');
      return;
    }
    setSaving(true);
    try {
      // Auto-fill actual_delivery_date if status is Delivered and field is blank
      const payload = { ...form };
      if (payload.status === 'Delivered' && !payload.actual_delivery_date) {
        payload.actual_delivery_date = new Date().toISOString().slice(0, 10);
      }
      if (payload.courier_partner !== 'Staff') payload.staff_member_name = '';
      payload.cost = Number(payload.cost || 0);
      const res = await fetch(`${API}/couriers${editingId ? `/${editingId}` : ''}`, {
        method: editingId ? 'PUT' : 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const saved = await res.json();
        toast.success(editingId ? `Courier record ${editingId} updated` : `Courier record ${saved.courier_id} added`);
        setDialogOpen(false);
        setEditingId(null);
        setForm(emptyForm);
        if (!editingId) setPage(1);
        fetchItems();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save courier record');
      }
    } catch (_e) {
      toast.error('Failed to save courier record');
    } finally {
      setSaving(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery(''); setDateFrom(''); setDateTo('');
    setStatusFilter(''); setPartnerFilter(''); setDocTypeFilter('');
    setPage(1);
  };

  if (!hasAccess) {
    return (
      <Layout>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Access restricted. Courier Details is available to Admin Head, Admin Team, and Super Admin.</p>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  const activeFilters = [searchQuery, dateFrom, dateTo, statusFilter, partnerFilter, docTypeFilter].filter(Boolean).length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Layout>
      <div className="space-y-6" data-testid="courier-details-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Truck className="h-6 w-6" />
              Courier Details
            </h1>
            <p className="text-muted-foreground">Track outbound couriers — dispatch, delivery, and returns.</p>
          </div>
          <Button onClick={openCreateDialog} data-testid="add-courier-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Courier
          </Button>
        </div>

        {/* Filter bar */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    placeholder="ID, AWB, receiver, location..."
                    className="w-[230px] h-8 text-xs pl-7"
                    data-testid="filter-search"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Dispatch From</Label>
                <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="w-[145px] h-8 text-xs" data-testid="filter-date-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To</Label>
                <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="w-[145px] h-8 text-xs" data-testid="filter-date-to" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={statusFilter || 'all'} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
                  <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {opts.statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Partner</Label>
                <Select value={partnerFilter || 'all'} onValueChange={(v) => { setPartnerFilter(v === 'all' ? '' : v); setPage(1); }}>
                  <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="filter-partner"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {opts.courier_partners.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Doc Type</Label>
                <Select value={docTypeFilter || 'all'} onValueChange={(v) => { setDocTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
                  <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="filter-doc-type"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {opts.document_types.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {activeFilters > 0 && (
                <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={clearFilters} data-testid="clear-filters">
                  <X className="w-3 h-3 mr-1" /> Clear ({activeFilters})
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Records</CardTitle>
            <CardDescription>{total} total</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm">No courier records match your filters.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Dispatch Date</TableHead>
                      <TableHead>Raised By</TableHead>
                      <TableHead>Receiver</TableHead>
                      <TableHead>Drop Location</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Request Type</TableHead>
                      <TableHead>Partner</TableHead>
                      <TableHead>AWB / Tracking</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Delivered On</TableHead>
                      <TableHead>Doc Type</TableHead>
                      <TableHead className="text-right">Cost (₹)</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((c) => (
                      <TableRow key={c.courier_id} className="cursor-pointer hover:bg-muted/40" onClick={() => setViewItem(c)} data-testid={`courier-row-${c.courier_id}`}>
                        <TableCell className="font-medium">{c.courier_id}</TableCell>
                        <TableCell>{c.dispatch_date}</TableCell>
                        <TableCell>{c.request_raised_by}</TableCell>
                        <TableCell>{c.received_by}</TableCell>
                        <TableCell>{c.drop_delivery_location}</TableCell>
                        <TableCell>{c.courier_type}</TableCell>
                        <TableCell>{c.request_type ? <Badge variant="outline">{c.request_type}</Badge> : '-'}</TableCell>
                        <TableCell>{c.courier_partner}{c.courier_partner === 'Staff' && c.staff_member_name ? ` — ${c.staff_member_name}` : ''}</TableCell>
                        <TableCell className="font-mono text-xs">{c.awb_tracking_number || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[c.status] || 'outline'}>{c.status}</Badge>
                        </TableCell>
                        <TableCell>{c.actual_delivery_date || '-'}</TableCell>
                        <TableCell>{c.document_type || '-'}</TableCell>
                        <TableCell className="text-right">{c.cost ? `₹${Number(c.cost).toLocaleString('en-IN')}` : '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={(e) => { e.stopPropagation(); openEditDialog(c); }}
                            data-testid={`edit-courier-${c.courier_id}`}
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 text-xs">
                <span className="text-muted-foreground">Page {page} of {totalPages}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Courier dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? `Edit Courier Record — ${editingId}` : 'Add Courier Record'}</DialogTitle>
            <DialogDescription>Capture courier dispatch details. Fields marked * are required.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="space-y-1">
              <Label>Dispatch Date *</Label>
              <Input type="date" value={form.dispatch_date} onChange={(e) => setForm({ ...form, dispatch_date: e.target.value })} data-testid="form-dispatch-date" />
            </div>
            <div className="space-y-1">
              <Label>Request Raised By *</Label>
              <Input value={form.request_raised_by} onChange={(e) => setForm({ ...form, request_raised_by: e.target.value })} placeholder="Name of requester" data-testid="form-raised-by" />
            </div>
            <div className="space-y-1">
              <Label>Receiver *</Label>
              <Input value={form.received_by} onChange={(e) => setForm({ ...form, received_by: e.target.value })} placeholder="Receiver name" data-testid="form-receiver" />
            </div>
            <div className="space-y-1">
              <Label>Drop Delivery Location *</Label>
              <Input value={form.drop_delivery_location} onChange={(e) => setForm({ ...form, drop_delivery_location: e.target.value })} placeholder="Full address / city / branch" data-testid="form-drop-location" />
            </div>
            <div className="space-y-1">
              <Label>Courier Type *</Label>
              <Input value={form.courier_type} onChange={(e) => setForm({ ...form, courier_type: e.target.value })} placeholder="Document / Parcel / Sample..." data-testid="form-courier-type" />
            </div>
            <div className="space-y-1">
              <Label>Courier Request Type *</Label>
              <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
                <SelectTrigger data-testid="form-request-type"><SelectValue placeholder="Select request type" /></SelectTrigger>
                <SelectContent>
                  {opts.request_types.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>AWB / Tracking Number</Label>
              <Input value={form.awb_tracking_number} onChange={(e) => setForm({ ...form, awb_tracking_number: e.target.value })} placeholder="Optional" data-testid="form-awb" />
            </div>
            <div className="space-y-1">
              <Label>Dispatcher / Courier Partner *</Label>
              <Select value={form.courier_partner} onValueChange={(v) => setForm({ ...form, courier_partner: v, staff_member_name: v === 'Staff' ? form.staff_member_name : '' })}>
                <SelectTrigger data-testid="form-partner"><SelectValue placeholder="Select partner" /></SelectTrigger>
                <SelectContent>
                  {opts.courier_partners.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.courier_partner === 'Staff' && (
              <div className="space-y-1">
                <Label>Name of Staff Member *</Label>
                <Input value={form.staff_member_name} onChange={(e) => setForm({ ...form, staff_member_name: e.target.value })} placeholder="Staff member name" data-testid="form-staff-name" />
              </div>
            )}
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {opts.statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Actual Delivery Date</Label>
              <Input type="date" value={form.actual_delivery_date} onChange={(e) => setForm({ ...form, actual_delivery_date: e.target.value })} data-testid="form-delivery-date" />
              {form.status === 'Delivered' && !form.actual_delivery_date && (
                <p className="text-[10px] text-muted-foreground">Will auto-fill today's date on save.</p>
              )}
            </div>
            <div className="space-y-1">
              <Label>Document Type</Label>
              <Select value={form.document_type || 'none'} onValueChange={(v) => setForm({ ...form, document_type: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="form-doc-type"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {opts.document_types.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Cost (₹)</Label>
              <Input type="number" min="0" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="0" data-testid="form-cost" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={submitForm} disabled={saving} data-testid="form-submit">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewItem} onOpenChange={(open) => !open && setViewItem(null)}>
        <DialogContent className="max-w-xl">
          {viewItem && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5" /> {viewItem.courier_id}
                </DialogTitle>
                <DialogDescription>Courier dispatch record</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm py-2">
                <div><span className="text-muted-foreground text-xs">Dispatch Date</span><p>{viewItem.dispatch_date}</p></div>
                <div><span className="text-muted-foreground text-xs">Status</span><p><Badge variant={STATUS_VARIANT[viewItem.status] || 'outline'}>{viewItem.status}</Badge></p></div>
                <div><span className="text-muted-foreground text-xs">Raised By</span><p>{viewItem.request_raised_by}</p></div>
                <div><span className="text-muted-foreground text-xs">Receiver</span><p>{viewItem.received_by}</p></div>
                <div className="col-span-2"><span className="text-muted-foreground text-xs">Drop Delivery Location</span><p>{viewItem.drop_delivery_location}</p></div>
                <div><span className="text-muted-foreground text-xs">Courier Type</span><p>{viewItem.courier_type}</p></div>
                <div><span className="text-muted-foreground text-xs">Request Type</span><p>{viewItem.request_type || '-'}</p></div>
                <div><span className="text-muted-foreground text-xs">Partner</span><p>{viewItem.courier_partner}</p></div>
                {viewItem.courier_partner === 'Staff' && (
                  <div><span className="text-muted-foreground text-xs">Staff Member</span><p>{viewItem.staff_member_name || '-'}</p></div>
                )}
                <div><span className="text-muted-foreground text-xs">AWB / Tracking</span><p className="font-mono text-xs">{viewItem.awb_tracking_number || '-'}</p></div>
                <div><span className="text-muted-foreground text-xs">Actual Delivery</span><p>{viewItem.actual_delivery_date || '-'}</p></div>
                <div><span className="text-muted-foreground text-xs">Document Type</span><p>{viewItem.document_type || '-'}</p></div>
                <div><span className="text-muted-foreground text-xs">Cost</span><p className="flex items-center gap-1"><IndianRupee className="h-3 w-3" />{Number(viewItem.cost || 0).toLocaleString('en-IN')}</p></div>
                <div className="col-span-2 pt-2 border-t mt-2">
                  <span className="text-muted-foreground text-xs">Created By</span>
                  <p>{viewItem.created_by_name || viewItem.created_by} · {viewItem.created_at?.slice(0,10)}</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setViewItem(null)}>Close</Button>
                <Button onClick={() => openEditDialog(viewItem)} data-testid="view-edit-btn">
                  <Pencil className="h-4 w-4 mr-2" /> Edit
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
